import { useEffect, useState } from 'react';
import { sessionStats, summarySentence } from '../../core/stats';
import { db, type SessionRecord } from '../../engine/db';
import { navigate, useApp } from '../app-state';
import { fmtDate, fmtPct, fmtTime, Notice } from '../components/bits';
import { Icon } from '../components/Icon';

interface SessionSummary {
  session: SessionRecord;
  sentence: string;
  cleanRate: number | null;
}

export async function loadSummaries(limit = Infinity): Promise<SessionSummary[]> {
  const sessions = (await db.listSessions()).slice(0, limit);
  const out: SessionSummary[] = [];
  for (const s of sessions) {
    const attempts = await db.attemptsForSession(s.id);
    if (!attempts.length) continue;
    const stats = sessionStats(attempts.map((a) => a.analysis), s.params);
    out.push({ session: s, sentence: summarySentence(stats), cleanRate: stats.cleanRate });
  }
  return out;
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function Home() {
  const app = useApp();
  const cal = app.active;
  const [recent, setRecent] = useState<SessionSummary[] | null>(null);
  useEffect(() => {
    void loadSummaries(4).then(setRecent);
  }, []);

  return (
    <div className="page stack lg">
      <div className="row between">
        <div className="row" style={{ gap: 10 }}>
          <span style={{ color: 'var(--accent)' }}>
            <Icon name="wave" size={28} />
          </span>
          <div>
            <h1 style={{ fontSize: 24 }}>SurfDaddy</h1>
            <p className="small faint">Pop-up trainer</p>
          </div>
        </div>
      </div>

      {!cal ? (
        <div className="card raised stack lg" style={{ padding: 22 }}>
          <div className="stack sm">
            <h2 style={{ fontSize: 24 }}>Did you land where you meant to?</h2>
            <p className="muted">
              Prop your phone up, tape an H on the floor, and practise pop-ups. After each one you’ll hear where your feet landed, whether you had to shuffle afterwards — or
              that the camera couldn’t tell.
            </p>
          </div>
          <ol className="stack sm" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
            {[
              ['tape', 'Tape the floor', 'Two crosswise strips where your feet land.'],
              ['camera', 'Place the phone', 'On a mount at waist height, 2.5–3 m away, on the side you face.'],
              ['target', 'Record your stance', 'Stand on your marks; that becomes your target.'],
            ].map(([icon, title, text], i) => (
              <li key={title} className="row" style={{ alignItems: 'flex-start' }}>
                <span className="icon-btn" style={{ width: 38, height: 38, borderRadius: 12 }}>
                  <Icon name={icon as 'tape'} size={19} />
                </span>
                <div>
                  <b>
                    {i + 1}. {title}
                  </b>
                  <p className="small muted">{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="stack sm">
            <button className="btn primary hero block" onClick={() => navigate('setup')}>
              <Icon name="camera" /> Set up with your camera
            </button>
            <button className="btn block" onClick={() => navigate('setup?demo=1')}>
              <Icon name="sparkle" /> Try the demo — no camera needed
            </button>
          </div>
        </div>
      ) : (
        <div className="card raised stack" style={{ padding: 20 }}>
          <div className="row between">
            <div>
              <h2>Ready to practise</h2>
              <p className="small muted" style={{ marginTop: 2 }}>
                {cal.stance === 'regular' ? 'Regular' : 'Goofy'} · {cal.tape ? 'taped floor' : 'no tape'} · {cal.source === 'demo' ? 'demo scene' : cal.camera.label ?? 'camera'} · set up {fmtDate(cal.createdAt)}
              </p>
            </div>
          </div>
          {cal.warnings.length > 0 && <Notice kind="warn">{cal.warnings[0]}</Notice>}
          <button className="btn primary hero block" onClick={() => navigate('practice')}>
            <Icon name="play" /> Start a practice set
          </button>
          <div className="row">
            <button className="btn grow" onClick={() => navigate('setup?recal=1')}>
              <Icon name="refresh" size={18} /> Recalibrate
            </button>
            <button className="btn grow" onClick={() => navigate('setup')}>
              New setup
            </button>
          </div>
          {cal.source === 'demo' && (
            <p className="tiny faint">
              You’re using the demo scene. When you’re ready, choose <b>New setup</b> to use your camera.
            </p>
          )}
        </div>
      )}

      {!isStandalone() && isIOS() && (
        <Notice>
          For full-screen practice and to keep your history safe, tap <b>Share</b> then <b>Add to Home Screen</b>.
        </Notice>
      )}

      {recent && recent.length > 0 && (
        <div>
          <div className="row between">
            <div className="section-title">Recent sessions</div>
            <a className="small faint" href="#/progress">
              All
            </a>
          </div>
          <div className="card flush list">
            {recent.map(({ session, sentence, cleanRate }) => (
              <a key={session.id} className="list-item" href={`#/session/${session.id}`}>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>
                    {fmtDate(session.startedAt)} <span className="faint">· {fmtTime(session.startedAt)}</span>
                    {session.mode === 'experiment' && <span className="faint"> · validation</span>}
                  </div>
                  <div className="small muted">{sentence}</div>
                </div>
                <div className="num" style={{ fontWeight: 650 }} aria-label="Clean rate">
                  {fmtPct(cleanRate)}
                </div>
                <Icon name="chevron" size={18} className="faint" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
